import { Injectable } from '@angular/core';
import { CodeNameModel } from '../models/code-name.model';
import { UserModel } from '../models/user.model';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { VersionAwareArtworkFile } from '../../requests/models/card-design-request.model';
import { ARTWORK_STATUSES } from '../../requests/constants/artwork-file-statuses.constants';
import { HttpHeaders } from '@angular/common/http';
import {ApprovalManagerService} from './approval-manager/approval-manager.service';
import {Warehouse} from '../../requests/models/warehouse.model';
import isEqual from 'lodash/isEqual';
import { FileSizeRestriction } from '../models/upload-file-restrictions.model';
import BigJs from 'big.js';
import sortBy from 'lodash/sortBy';
import { Observable, OperatorFunction, pipe } from 'rxjs';
import { combineLatest, first, map, skip, startWith, withLatestFrom } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class UtilsService {

  constructor(private fb: FormBuilder) {}

  public static merchantValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      if (!control.value?.name) {
        return { required: true };
      }

      return null;
    };
  }

  public static userValidationFn(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      if (control.value && typeof(control.value) === 'string') {
        return { userValidator: true };
      }

      return null;
    };
  }

  public static availableQtyValidationFn(total: AbstractControl, formArr: FormArray): ValidatorFn {
    return (): { [key: string]: boolean } | null  => {
      const sum = formArr.getRawValue().reduce((acc, curr) => acc + Number(curr.quantity), 0);
      if (Number(total.value) !== Number(sum)) {
        return { hasNotZeroSum: true };
      }

      return null;
    };
  }

  public static availableArrayQtyValidationFn(formGroup: FormGroup, formArr: FormArray): ValidatorFn {
    return (): { [key: string]: boolean } | null  => {

      const versionQuantities = (formGroup.get('versionQuantities') as FormArray);
      const compareSum = versionQuantities.length ?
        versionQuantities.getRawValue().reduce((acc, curr) => acc + Number(curr.quantity), 0) :
        formGroup.get('productionQuantity').value;

      const sum = formArr.getRawValue().reduce((acc, curr) => acc + Number(curr.quantity), 0);
      if (Number(compareSum) !== Number(sum)) {
        return { hasNotZeroSum: true };
      }

      return null;
    };
  }

  public static multiplicityValidationFn(multiplier: number): ValidatorFn {
    return (control): { [key: string]: boolean } | null  => {
      if (control.value === null || control.value === '') {
        return null;
      }

      const value = Number(control.value);
      if (value && !(value % multiplier)) {
        return null;
      }

      return { multiplicity: true };
    };
  }

  public static artworksApprovedValidationFn(approvalManagerService: ApprovalManagerService): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      const { artworkFiles }: { artworkFiles: VersionAwareArtworkFile[]} = control.value;

      if (artworkFiles?.length) {
        const isAllApproved = approvalManagerService.getArtworkFileStatuses(artworkFiles)
          .every(status => status === ARTWORK_STATUSES.approved);
        return isAllApproved ? null : { notAllApproved: true };
      }

      return null;
    };
  }

  public static artworksNotRejectedValidationFn(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      const { artworkFiles }: { artworkFiles: VersionAwareArtworkFile[]} = control.value;

      if (artworkFiles?.length) {
        const isAnyRejected = artworkFiles.some(af =>
          af.versions[0].resolutions[0].status === ARTWORK_STATUSES.rejected);
        return  isAnyRejected ? {rejected: isAnyRejected} : null;
      }

      return null;
    };
  }

  public static bulkFormValidationFn(validationObject): ValidatorFn {
    return (ac: AbstractControl) => {
      if (
        isEqual(ac.value, validationObject)
      ) {
        return {
          noChanges: true
        };
      }
      return null;
    };
  }

  public static isTrueExist(): ValidatorFn {
    return (arr: FormArray): { [key: string]: boolean } | null => {
      if (arr.value && !arr.value.includes(true)) {
        return { allInFalse: true };
      }

      return null;
    };
  }

  public static maxCalculationValidator(evaluator: (val: number) => number,  max: number, fieldName: string) {
    return (ac: AbstractControl) => {
      if(ac?.value) {
        const actual = evaluator(ac.value);
        if(evaluator(ac.value) > max) {
          return {
            maxCalculation: {
              fieldName,
              max,
              actual
            }
          };
        }
      }
      return null;
    };
  }

  public static printerInfoValidationFn(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      if (control.value?.mdmId) {
        return null;
      }
      return { printerInfoValidator: true };
    };
  }

  public static startRangeValidationFn(endControl: AbstractControl): ValidatorFn {
    return (startControl: AbstractControl): { [key: string]: boolean } | null  => {
      const { value: start }: { value: string | null } = startControl;
      const { value: end }: { value: string | null } = endControl;
      const bigStart = new BigJs(start || '0');
      const bigEnd = new BigJs(end || '0');

      if (start && end) {
        if (bigEnd.gt(bigStart) && start.length === end.length) {
          startControl.markAsUntouched();
          return null;
        }
        return { startRange: true };
      }

      return null;
    };
  }

  public static endRangeValidationFn(startControl: AbstractControl): ValidatorFn {
    return (endControl: AbstractControl): { [key: string]: boolean } | null  => {
      const { value: start }: { value: string | null } = startControl;
      const { value: end }: { value: string | null } = endControl;
      const bigStart = new BigJs(start || '0');
      const bigEnd = new BigJs(end || '0');

      if (start && end) {
        if (bigEnd.gt(bigStart) && start.length === end.length) {
          endControl.markAsUntouched();
          return null;
        }
        return { endRange: true };
      }

      return null;
    };
  }

  public static packageIdTBDValidationFn(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      if (control.value?.toUpperCase() === 'TBD') {
        return { packageIdTBDValidator: true };
      }
      return null;
    };
  }

  public static checkboxTrueValidationFn(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      if (control.value === true) {
        return null;
      }
      return { checkboxTrueValidator: true };
    };
  }

  public static productionQtyValidationFn(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      const masterCaseSizeControl = control.parent?.get('masterCaseSize');
      if (control.value && masterCaseSizeControl?.value && control.value % masterCaseSizeControl?.value !== 0) {
        return { productionQtyValidator: true };
      }
        return null;

    };
  }

  public static cardProductionQtyValidationFn(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      const caseSizeControl = control.parent?.get('caseSize');
      if (control.value && caseSizeControl?.value && control.value % caseSizeControl?.value !== 0) {
        return { cardProductionQtyValidator: true };
      }
        return null;

    };
  }

  public static getFileNameFromHeaders(headers: HttpHeaders): string {
    return decodeURIComponent(headers.get('file-name'));
  }
  public static sortByFields<T>(items: T[], sortFields: Array<keyof T> | ((val: any) => number)): T[] {
    return sortBy(items, sortFields);
  }

  public static isFileFitsBySize(bytes, fileSize: FileSizeRestriction): boolean {
    if (bytes === 0) {
      return true;
    }
    const k = 1024;
    const dm = 2;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const restrictionSizeTypeIndex = sizes.indexOf(fileSize.sizeType);

    const fileSizeTypeIndex = Math.floor(Math.log(bytes) / Math.log(k));

    if (fileSizeTypeIndex < restrictionSizeTypeIndex) {
      return true;
    }

    if (fileSizeTypeIndex > restrictionSizeTypeIndex) {
      return false;
    }

    if (fileSizeTypeIndex === restrictionSizeTypeIndex) {
      const revertedSize = parseFloat((bytes / Math.pow(k, fileSizeTypeIndex)).toFixed(dm));
      return revertedSize <= fileSize.sizeNumber;
    }
  }

  public static getFilesAggregatedStatus(files: Array<any>): 'progress' | 'rejected' | 'approved' {
    if (files && files.length) {
      if (files.every(file => file.status === ARTWORK_STATUSES.rejected)) {
        return 'rejected';
      } else if (files.every(file => file.status === ARTWORK_STATUSES.approved)) {
        return 'approved';
      }
    }
    return 'progress';
  }

  public static maxBytesLength(maxBytesLength: number, maxBytesLabel: string) {
    return (ac: AbstractControl) => {
      const value = ac?.value;
      if(value && window.TextEncoder) {
        const encoder = new TextEncoder();
        const bytes = encoder.encode(value);

        if(bytes.length > maxBytesLength) {
          return {
            maxlength: {
              requiredLength: maxBytesLabel
            }
          };
        }
      }
      return null;
    };
  }

  public static getWarehouseName(warehouse: Partial<Warehouse>): string {
    return warehouse?.name ?
      (typeof warehouse?.name === 'string' ? warehouse?.name : (warehouse?.name as Warehouse)?.name) :
      '';
  }

  public static fileTypeAccepted(file: File, fileTypesToAccept: string[]) {
    if (!fileTypesToAccept || fileTypesToAccept.length === 0) {
      return true;
    }
    const fileTypeAccepted = fileTypesToAccept.indexOf(file.type) > -1;
    const [,fileExtension] = /(?:\.([^.]+))?$/.exec(file.name);
    const fileExtensionAccepted = fileTypesToAccept.indexOf(`.${fileExtension.toLowerCase()}`) > -1;

    return fileTypeAccepted || fileExtensionAccepted;
  }

  public static notEmptyValidator: ValidatorFn = (ac) => {
    const value = ac?.value || '';
    if(/^(\s+\S+\s*)*(?!\s).*$/.test(value)) {
      return null;
    }
    return {
      notEmpty: true
    };
  };

  public getItemName(array: Partial<CodeNameModel>[], code: string): string {
    return array?.find(item => item.code === code)?.name || code;
  }

  public static getFullUserName(user: UserModel): string {
    let fullName = '';
    if (user?.firstName) {
      fullName = `${user.firstName} `;
    }
    if (user?.lastName) {
      fullName += user.lastName;
    }
    return fullName.trim();
  }
  public getFullUserName(user: UserModel): string {
    let fullName = '';
    if (user?.firstName) {
      fullName = `${user.firstName} `;
    }
    if (user?.lastName) {
      fullName += user.lastName;
    }
    return fullName.trim();
  }

  public static requiredIfSiblingTrue(siblingName: string): ValidatorFn {
    return (ac: AbstractControl) => {
      if (ac?.parent?.get(siblingName)?.valid && ac?.parent?.get(siblingName)?.value === true) {
        return Validators.required(ac);
      }
      return null;
    };
  }

  public getFormattedBytes(bytes: number, decimals = 2): string {
    if (bytes <= 0) {
      return '0 Bytes';
    }

    const k = 1024;
    const dm = decimals <= 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
  }

  public static getFormattedBytes(bytes: number, decimals = 2): string {
    if (bytes <= 0) {
      return '0 Bytes';
    }

    const k = 1024;
    const dm = decimals <= 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
  }

  public getParentChild(data: any[]): any[] {
    const formatted = [];
    data.sort((a, b) => {
      const codeA = a.code.toUpperCase();
      const codeB = b.code.toUpperCase();
      if (codeA < codeB) { return -1; }
      if (codeA > codeB) {return 1; }
      return 0;
    });
    data.forEach(item => {
      if (item.code === 'PRJ' && item.name.indexOf('(PRJ)') === -1) {
        item.name += ' (PRJ)';
      }
      if (!item.parentCode) {
        formatted.push({...item, children: []});
      }
    });
    data.forEach(child => {
      if (child.parentCode) {
        formatted.find(parent => parent.code === child.parentCode)?.children.push(child);
      }
    });
    return formatted;
  }

  public getMerchantName(option: any, merchant?: FormGroup): string {
    if (option?.name) {
      return `${option.name} - ${option.fcmsId ? option.fcmsId : (option.mdmId ? option.mdmId : option.id)}`;
    }
      const id = merchant?.controls?.id?.value;
      const mdmId = merchant?.controls?.mdmId?.value;
      const fcmsId = merchant?.controls?.fcmsId?.value;
      if (option && typeof option === 'string' && (fcmsId || mdmId || id)) {
        return `${option} - ${fcmsId ? fcmsId : (mdmId ? mdmId : id)}`;
      }  return '';

  }

  public sortAZNames(a, b): number {
      const nameA = a.name.toUpperCase();
      const nameB = b.name.toUpperCase();

      if (nameA < nameB) {
        return -1;
      }
      if (nameA > nameB) {
        return 1;
      }
      return 0;
  }

  public sortByDate(a: Partial<{createdAt: string}>, b: Partial<{createdAt: string}>): number {
    const date1 = new Date(a.createdAt);
    const date2 = new Date(b.createdAt);

    return Number(date2) - Number(date1);
  }

  public codeNameMap(codeNames: CodeNameModel[]): { [key: string]: string } {
    return codeNames.reduce((acc, codeName) => ({...acc, [codeName.code]: codeName.name}), {});
  }

  public logInvalidControls(form: FormGroup, path: string = ''): void {
    const controls = form.controls;
    for (const name of Object.keys(controls)) {
      if ((controls[name] as FormGroup).controls) {
        this.logInvalidControls((controls[name] as FormGroup), [path, name].join('.'));
      } else {
        if (controls[name].invalid) {
          console.log('invalid ', [path, name].join('.'));
        }
      }
    }
  }

  public touchFormControls(form: FormGroup): void {
    Object.keys(form.controls).forEach(key => {
      const control = form.get(key);
      if ((control as FormGroup).controls) {
        this.touchFormControls((control as FormGroup));
      } else if (control.pristine) {
        control.markAsTouched();
        if (key !== 'name' || (control.invalid && key === 'name')) {
          control.updateValueAndValidity({onlySelf: true, emitEvent: true});
        }
      }
    });
  }

  consoleListPrinters(printers: any[]): void {
    function isIncludesType(item, type) {
      return item.types.includes(type) ? '+' : null;
    }
    console.table([...printers].map(printer => ({
        name: printer.name,
        INTERNAL: isIncludesType(printer, 'INTERNAL'),
        EXTERNAL: isIncludesType(printer, 'EXTERNAL'),
        CERTIFIED_WRAP: isIncludesType(printer, 'CERTIFIED_WRAP'),
        CERTIFIED_MULTIPACK: isIncludesType(printer, 'CERTIFIED_MULTIPACK'),
      }))
    );
  }


  public setMerchantsDataToControls(reqItems, itemsControlsArray: FormArray): void {
    reqItems.forEach((item, idx) => {
      const itemForm = itemsControlsArray.at(idx);
      itemForm.patchValue(item);
      const merchantsArray = (itemForm.get('merchants') as FormArray);
      merchantsArray.clear();

      item.merchants.forEach(merchant => {
        merchantsArray.push(this.fb.group({
          id: this.fb.control(merchant.id),
          mdmId: this.fb.control(merchant.mdmId),
          name: this.fb.control({ name: merchant.name, id: merchant.fcmsId ? merchant.fcmsId : merchant.mdmId }),
          fcmsId: this.fb.control(merchant.fcmsId, Validators.required),
          country: this.fb.control(merchant.country, Validators.required),
          accountManagers: this.fb.array(merchant.accountManagers)
        }));
      });
    });
  }

}

export function delayedWithLatestFrom<T, N>(target$: Observable<N>): OperatorFunction<T, [T, N]> {
  // special value to avoid accidental match with values that could originate from target$
  const uniqueSymbol = Symbol('withLatestFromIgnore');

  return pipe(
    // emit as soon target observable emits the first value
    combineLatest(target$.pipe(first())),
    // skip the first emission because it's handled above, and then continue like a normal `withLatestFrom` operator
    withLatestFrom(target$.pipe(skip(1), startWith(uniqueSymbol))),
    map(([[rest, combineLatestValue], withLatestValue]) => {
      // take combineLatestValue for the first time, and then always take withLatestValue
      const appendedValue = withLatestValue === uniqueSymbol ? combineLatestValue : withLatestValue;

      return [rest, appendedValue];
    })
  );
}
